import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { ExpenseListCard } from './components/ExpenseListCard';
import type { Expense } from '../../types/car';
import { useLanguage } from '../../contexts/LanguageContext';

type DateRange = { from: Date | undefined; to?: Date | undefined };
type ExpenseTypeFilter = '' | 'STORE' | 'CAR';
type CurrencyFilter = '' | 'IQD' | 'USD';

function parseDateRangeFromUrl(searchParams: URLSearchParams): DateRange | undefined {
  const from = searchParams.get('fromDate');
  const to = searchParams.get('toDate');
  if (!from && !to) return undefined;
  const fromDate = from ? new Date(from) : undefined;
  const toDate = to ? new Date(to) : undefined;
  if (fromDate?.toString() === 'Invalid Date' && toDate?.toString() === 'Invalid Date') return undefined;
  return { from: fromDate ?? undefined, to: toDate ?? undefined };
}

function buildSearchParams(
  search: string,
  expenseType: ExpenseTypeFilter,
  currencyFilter: CurrencyFilter,
  dateRange: DateRange | undefined
): URLSearchParams {
  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (expenseType === 'STORE' || expenseType === 'CAR') params.set('expenseType', expenseType);
  if (currencyFilter === 'IQD' || currencyFilter === 'USD') params.set('currency', currencyFilter);
  if (dateRange?.from) params.set('fromDate', dateRange.from.toISOString());
  if (dateRange?.to) params.set('toDate', dateRange.to.toISOString());
  return params;
}

const PAGE_SIZE = 50;

export default function ExpensesIndex() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalAmountByCurrency, setTotalAmountByCurrency] = useState<Record<string, number>>({});
  const [search, setSearch] = useState(() => searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') ?? '');
  const [expenseTypeFilter, setExpenseTypeFilter] = useState<ExpenseTypeFilter>(() => {
    const t = searchParams.get('expenseType') ?? '';
    return t === 'STORE' || t === 'CAR' ? t : '';
  });
  const [currencyFilter, setCurrencyFilter] = useState<CurrencyFilter>(() => {
    const c = searchParams.get('currency') ?? '';
    return c === 'IQD' || c === 'USD' ? c : '';
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    parseDateRangeFromUrl(searchParams)
  );

  // Sync state from URL only when URL actually changed (e.g. back/forward) — avoid overwriting on our own setSearchParams
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') ?? '';
    const typeFromUrl = (searchParams.get('expenseType') ?? '') as ExpenseTypeFilter;
    const typeResolved = typeFromUrl === 'STORE' || typeFromUrl === 'CAR' ? typeFromUrl : '';
    const currencyFromUrl = (searchParams.get('currency') ?? '') as CurrencyFilter;
    const currencyResolved = currencyFromUrl === 'IQD' || currencyFromUrl === 'USD' ? currencyFromUrl : '';
    const rangeFromUrl = parseDateRangeFromUrl(searchParams);

    setSearch((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));
    setDebouncedSearch((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));
    setExpenseTypeFilter((prev) => (prev !== typeResolved ? typeResolved : prev));
    setCurrencyFilter((prev) => (prev !== currencyResolved ? currencyResolved : prev));
    setDateRange((prev) => {
      if (!prev && !rangeFromUrl) return prev;
      if (!prev || !rangeFromUrl) return rangeFromUrl ?? undefined;
      const prevFrom = prev.from?.getTime();
      const prevTo = prev.to?.getTime();
      const nextFrom = rangeFromUrl.from?.getTime();
      const nextTo = rangeFromUrl.to?.getTime();
      if (prevFrom === nextFrom && prevTo === nextTo) return prev;
      return rangeFromUrl ?? undefined;
    });
  }, [searchParams]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Sync filters to URL when they change (replace: true so back button doesn't step through every filter change)
  useEffect(() => {
    const next = buildSearchParams(debouncedSearch, expenseTypeFilter, currencyFilter, dateRange);
    const current = new URLSearchParams(searchParams);
    if (next.toString() !== current.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [debouncedSearch, expenseTypeFilter, currencyFilter, dateRange, setSearchParams, searchParams]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setExpenseTypeFilter('');
    setCurrencyFilter('');
    setDateRange(undefined);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const refetch = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('page', '1');
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (expenseTypeFilter === 'CAR' || expenseTypeFilter === 'STORE') params.set('expenseType', expenseTypeFilter);
      if (currencyFilter === 'IQD' || currencyFilter === 'USD') params.set('currency', currencyFilter);
      if (dateRange?.from) params.set('fromDate', dateRange.from.toISOString());
      if (dateRange?.to) params.set('toDate', dateRange.to.toISOString());
      const data = await api.get(`/api/expenses?${params.toString()}`);
      const list = (data as { expenses?: Expense[] })?.expenses ?? data;
      setExpenses(Array.isArray(list) ? list : []);
      const pagination = (data as { pagination?: { totalCount: number } })?.pagination;
      setTotalCount(pagination?.totalCount ?? 0);
      setTotalAmount((data as { totalAmount?: number })?.totalAmount ?? 0);
      setTotalAmountByCurrency((data as { totalAmountByCurrency?: Record<string, number> })?.totalAmountByCurrency ?? {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [debouncedSearch, expenseTypeFilter, currencyFilter, dateRange?.from, dateRange?.to]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', String(PAGE_SIZE));
        params.set('page', '1');
        if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
        if (expenseTypeFilter === 'CAR' || expenseTypeFilter === 'STORE') params.set('expenseType', expenseTypeFilter);
        if (currencyFilter === 'IQD' || currencyFilter === 'USD') params.set('currency', currencyFilter);
        if (dateRange?.from) params.set('fromDate', dateRange.from.toISOString());
        if (dateRange?.to) params.set('toDate', dateRange.to.toISOString());
        const data = await api.get(`/api/expenses?${params.toString()}`);
        if (cancelled) return;
        const list = (data as { expenses?: Expense[] })?.expenses ?? data;
        setExpenses(Array.isArray(list) ? list : []);
        const pagination = (data as { pagination?: { totalCount: number } })?.pagination;
        setTotalCount(pagination?.totalCount ?? 0);
        setTotalAmount((data as { totalAmount?: number })?.totalAmount ?? 0);
        setTotalAmountByCurrency((data as { totalAmountByCurrency?: Record<string, number> })?.totalAmountByCurrency ?? {});
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, expenseTypeFilter, currencyFilter, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('page', String(Math.floor(expenses.length / PAGE_SIZE) + 1));
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (expenseTypeFilter === 'CAR' || expenseTypeFilter === 'STORE') params.set('expenseType', expenseTypeFilter);
      if (currencyFilter === 'IQD' || currencyFilter === 'USD') params.set('currency', currencyFilter);
      if (dateRange?.from) params.set('fromDate', dateRange.from.toISOString());
      if (dateRange?.to) params.set('toDate', dateRange.to.toISOString());
      const data = await api.get(`/api/expenses?${params.toString()}`);
      const list = (data as { expenses?: Expense[] })?.expenses ?? data;
      const nextList = Array.isArray(list) ? list : [];
      setExpenses((prev) => [...prev, ...nextList]);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [debouncedSearch, expenseTypeFilter, currencyFilter, dateRange?.from, dateRange?.to, expenses.length]);

  if (loading && expenses.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('expense.title')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('expense.subtitle')}</p>
          </div>
          <Button asChild variant="outline" className="border-slate-200">
            <Link to="/expenses/expense-category">{t('expense.expenseCategoryLink')}</Link>
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <ExpenseListCard
          expenses={expenses}
          totalAmount={totalAmount}
          totalCount={totalCount}
          totalAmountByCurrency={totalAmountByCurrency}
          onRefresh={refetch}
          onLoadMore={loadMore}
          isRefreshing={isRefreshing}
          isLoadingMore={isLoadingMore}
          search={search}
          setSearch={setSearch}
          expenseTypeFilter={expenseTypeFilter}
          setExpenseTypeFilter={setExpenseTypeFilter}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onClearFilters={clearFilters}
          hasActiveFilters={!!(search.trim() || expenseTypeFilter || currencyFilter || dateRange?.from || dateRange?.to)}
          currencyFilter={currencyFilter}
          setCurrencyFilter={setCurrencyFilter}
        />
      </main>
    </div>
  );
}
